'use client'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { LandingHero } from './_sections/LandingHero'
import { LandingSupervisionDeepDive } from './_sections/LandingSupervisionDeepDive'
import { LandingReferralsDeepDive } from './_sections/LandingReferralsDeepDive'
import { LandingTourNudge } from './_sections/LandingTourNudge'
import { LandingPillarsGrid } from './_sections/LandingPillarsGrid'
import { LandingPricing } from './_sections/LandingPricing'
import { LandingTestimonial } from './_sections/LandingTestimonial'
import { LandingCohortsAndFaq } from './_sections/LandingCohortsAndFaq'
import { LandingFinalCta } from './_sections/LandingFinalCta'

// Between Sessions landing — rebuilt from scratch after two iterations
// of "tour on a scroll page" failed to convey the actual value
// proposition. The problem those attempts had wasn't the copy, it was
// the frame: a tour teaches every feature equally over 14 slides; a
// landing has to bury the lede on a single pair of differentiators
// (supervision + referrals) that make Between Sessions worth paying for
// vs any other therapist community.
//
// This version:
//   - Free-scroll (no snap). Section heights vary by importance.
//   - Two flagship deep-dive sections (Supervision, Referrals) get the
//     visual weight of alternating text/visual layouts. The supporting
//     pillars (community, tools, practice support, clinical dev) are
//     compressed into a single 4-column grid.
//   - Hero headline names both differentiators in one line so a cold
//     visitor knows what BS is within 5 seconds.
//   - Pricing surfaces at position 5 (after the two deep dives that
//     justify the price) — clickable tier cards.
//   - Dark-background final CTA to signal "this is the decision moment."
//
// Tour lives at /between-sessions/tour untouched and stays the guided
// walkthrough for anyone who wants the ceremonial narrative depth.

export function BetweenSessionsLanding() {
  return (
    <div className="bg-[#faf9f6]">
      {/* Persistent wordmark — top-left, matches tour chrome. Fixed so
          it stays visible as the user scrolls the whole page. */}
      <Link
        href="/between-sessions"
        className="fixed top-5 left-6 sm:left-8 z-40 font-display font-bold text-[22px] sm:text-[26px] leading-none tracking-tight text-gray-900 hover:text-gray-700 transition-colors"
      >
        Between Sessions
      </Link>

      {/* Persistent nav cluster (top-right) — 'See how it works' link
          + Join pill. The link is an underlined text nav item adjacent
          to the primary Join CTA, maintaining visual hierarchy while
          keeping the walkthrough visible on every scroll. On mobile
          the label shortens to "How it works" to fit alongside "Join". */}
      <div className="fixed top-4 right-6 sm:right-8 z-40 flex items-center gap-4 sm:gap-5">
        <Link
          href="/between-sessions/tour"
          className="text-[13px] text-gray-700 hover:text-gray-900 font-display font-semibold underline underline-offset-4 decoration-gray-400 hover:decoration-gray-900 transition-colors whitespace-nowrap"
        >
          <span className="hidden sm:inline">See how it works</span>
          <span className="sm:hidden">How it works</span>
        </Link>
        <Link
          href="/between-sessions/tour/join"
          className="group inline-flex items-center gap-2 pl-4 pr-2 py-2 text-[13px] font-display font-semibold rounded-full bg-gray-900 hover:bg-black text-white shadow-[0_6px_16px_-6px_rgba(20,20,20,0.35)] hover:-translate-y-0.5 hover:shadow-[0_10px_22px_-10px_rgba(20,20,20,0.45)] transition-all duration-200"
        >
          <span className="hidden sm:inline">Join Between Sessions</span>
          <span className="sm:hidden">Join</span>
          <span className="w-6 h-6 rounded-full bg-white/15 group-hover:bg-white/25 flex items-center justify-center transition-colors">
            <ArrowRight className="w-3 h-3" />
          </span>
        </Link>
      </div>

      <main>
        <LandingHero />
        <LandingSupervisionDeepDive />
        <LandingReferralsDeepDive />
        <LandingTourNudge />
        <LandingPillarsGrid />
        <LandingPricing />
        <LandingTestimonial />
        <LandingCohortsAndFaq />
        <LandingFinalCta />
      </main>
    </div>
  )
}
